import React, { useState, useEffect } from 'react';
import { cn } from '../../utils/cn';
import Button from '../../components/ui/Button';
import Input from '../../components/ui/Input';
import Select from '../../components/ui/Select';
import { useAuth } from '../../contexts/AuthContext';
import { X, Calendar, User, Send, CheckCircle, AlertCircle, Loader2 } from 'lucide-react';

const AppointmentRequestModal = ({ isOpen, onClose }) => {
  const { session } = useAuth();
  const [step, setStep] = useState('form'); // 'form', 'loading', 'success', 'error'
  const [formData, setFormData] = useState({
    requesterName: '', // Auto-preenchido
    appointmentType: '',
    suggestedTime: '',
    suggestedDate: '',
    message: ''
  });
  const [errors, setErrors] = useState({});
  const [targetUser, setTargetUser] = useState({
    name: 'Lindsey',
    avatar: '/avatars/diane.jpg',
    title: 'Mentora Sênior'
  });
  const [availableSlots, setAvailableSlots] = useState([]);
  const [appointmentTypes, setAppointmentTypes] = useState([
    { 
      value: 'individual', 
      label: 'Atendimento Individual', 
      duration: '60 min',
      description: 'Sessão individual de mentoria' 
    },
    { 
      value: 'group', 
      label: 'Consultoria em Grupo', 
      duration: '90 min',
      description: 'Sessão em grupo até 5 pessoas' 
    },
    { 
      value: 'workshop', 
      label: 'Workshop', 
      duration: '120 min',
      description: 'Workshop temático' 
    }
  ]);
  const [loading, setLoading] = useState(false);
  const [characterCount, setCharacterCount] = useState(0);
  const maxCharacters = 500;

  // Auto-preenchimento do nome do usuário logado
  useEffect(() => {
    if (session?.profile?.name) {
      setFormData(prev => ({ ...prev, requesterName: session.profile.name }));
    } else if (session?.systemUser?.name) {
      setFormData(prev => ({ ...prev, requesterName: session.systemUser.name }));
    }
  }, [session]);

  // Função auxiliar para obter o próximo dia útil
  const getNextBusinessDay = (fromDate) => {
    const nextDay = new Date(fromDate);
    nextDay.setDate(fromDate.getDate() + 1);
    
    // Se for sábado (6) ou domingo (0), pular para segunda
    if (nextDay.getDay() === 0) nextDay.setDate(nextDay.getDate() + 1); // domingo -> segunda
    if (nextDay.getDay() === 6) nextDay.setDate(nextDay.getDate() + 2); // sábado -> segunda
    
    return nextDay;
  };

  // Função para gerar slots de um dia específico
  const generateSlotsForDay = (date, config) => {
    const slots = [];
    const startHour = parseInt(config.startTime.split(':')[0]);
    const endHour = parseInt(config.endTime.split(':')[0]);
    const slotDuration = config.duration / 60; // converter minutos para horas
    
    for (let hour = startHour; hour < endHour; hour += slotDuration) {
      const lastValidHour = endHour - slotDuration;
      if (hour <= lastValidHour) {
        const slotTime = new Date(date);
        slotTime.setHours(hour, 0, 0, 0);
        
        slots.push({
          value: slotTime.toISOString(),
          label: `${date.getDate()} ${date.toLocaleDateString('pt-BR', { month: 'short' })}, ${hour.toString().padStart(2, '0')}:00`,
          available: true,
          date: date,
          time: `${hour.toString().padStart(2, '0')}:00`
        });
      }
    }
    
    return slots;
  };

  // Generate weekly slots based on weekly patterns
  const generateWeeklySlots = () => {
    const slots = [];
    const today = new Date();
    const startDate = getNextBusinessDay(today);
    
    // Padrão semanal de disponibilidade (configurável)
    const weeklyPattern = {
      monday: { enabled: true, startTime: '14:00', endTime: '18:00', duration: 120 },
      tuesday: { enabled: true, startTime: '16:00', endTime: '20:00', duration: 120 },
      wednesday: { enabled: true, startTime: '16:00', endTime: '20:00', duration: 120 },
      thursday: { enabled: true, startTime: '16:00', endTime: '19:00', duration: 60 },
      friday: { enabled: true, startTime: '16:00', endTime: '19:00', duration: 60 },
      saturday: { enabled: false, startTime: '', endTime: '', duration: 0 },
      sunday: { enabled: false, startTime: '', endTime: '', duration: 0 }
    };

    // Gerar slots para as próximas 3 semanas a partir de startDate
    for (let week = 0; week < 3; week++) {
      Object.entries(weeklyPattern).forEach(([day, config]) => {
        if (config.enabled) {
          const dayOfWeek = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'].indexOf(day);
          const targetDate = new Date(startDate);
          targetDate.setDate(startDate.getDate() + (week * 7) + dayOfWeek);
          
          // Gerar slots para o dia
          const daySlots = generateSlotsForDay(targetDate, config);
          slots.push(...daySlots);
        }
      });
    }
    
    return slots.sort((a, b) => new Date(a.value) - new Date(b.value));
  };

  // Simulate fetching available slots based on selected appointment type
  useEffect(() => {
    if (formData?.appointmentType) {
      fetchAvailableSlots();
    }
  }, [formData?.appointmentType]);

  const fetchAvailableSlots = async () => {
    setLoading(true);
    try {
      // Simulate API call
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      // Usar slots semanais gerados em vez de mock slots fixos
      const generatedSlots = generateWeeklySlots();
      setAvailableSlots(generatedSlots);
    } catch (error) {
      console.error('Erro ao buscar horários:', error);
    } finally {
      setLoading(false);
    }
  };

  // Handle form field changes
  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    
    // Clear errors when user starts typing
    if (errors?.[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }

    // Update character count for message field
    if (field === 'message') {
      setCharacterCount(value?.length);
    }
  };

  // Validate form
  const validateForm = () => {
    const newErrors = {};

    if (!formData?.appointmentType) {
      newErrors.appointmentType = 'Selecione um tipo de atendimento';
    }

    if (!formData?.suggestedTime) {
      newErrors.suggestedTime = 'Selecione um horário';
    }

    if (formData?.message?.length > maxCharacters) {
      newErrors.message = `A mensagem deve ter no máximo ${maxCharacters} caracteres`;
    }

    setErrors(newErrors);
    return Object.keys(newErrors)?.length === 0;
  };

  // Handle form submission
  const handleSubmit = async (e) => {
    e?.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    setStep('loading');
    
    try {
      // Simulate API call
      await new Promise(resolve => setTimeout(resolve, 2000));
      
      console.log('Enviando solicitação:', formData);
      
      setStep('success');
    } catch (error) {
      console.error('Erro ao enviar solicitação:', error);
      setStep('error');
    }
  };

  // Close modal
  const handleClose = () => {
    // Reset form
    setStep('form');
    setFormData({
      requesterName: session?.profile?.name || session?.systemUser?.name || '',
      appointmentType: '',
      suggestedTime: '',
      suggestedDate: '',
      message: ''
    });
    setErrors({});
    setCharacterCount(0);
    onClose();
  };

  // Handle retry
  const handleRetry = () => {
    setStep('form');
  };

  // Get selected appointment type details
  const selectedAppointmentType = appointmentTypes?.find(type => type?.value === formData?.appointmentType);

  if (!isOpen) {
    return null;
  }

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-modal flex items-center justify-center p-4 z-50">
      <div className={cn(
        "bg-card rounded-lg shadow-tier-3 w-full max-w-md mx-auto transition-modal event-modal",
        "max-h-[90vh] overflow-y-auto"
      )}>
        {step === 'form' && (
          <>
            {/* Header */}
            <div className="flex items-center justify-between p-6 border-b border-border">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
                  <User className="w-5 h-5 text-primary" />
                </div>
                <div>
                  <h2 className="text-lg font-semibold text-foreground">
                    Solicitar Atendimento
                  </h2>
                  <p className="text-sm text-muted-foreground">
                    com {targetUser?.name}
                  </p>
                </div>
              </div>
              
              <Button
                variant="ghost"
                size="icon"
                onClick={handleClose}
                className="text-muted-foreground hover:text-foreground"
              >
                <X className="w-5 h-5" />
              </Button>
            </div>

            {/* Form */}
            <form onSubmit={handleSubmit} className="p-6 space-y-6">
              {/* Requester Name - Auto-filled */}
              <Input
                label="Nome do Solicitante"
                value={formData?.requesterName}
                disabled
                className="bg-muted"
                description="Seu nome será enviado automaticamente"
              />

              {/* Appointment Type */}
              <div className="space-y-2">
                <Select
                  label="Tipo de Atendimento"
                  value={formData?.appointmentType}
                  onChange={(value) => handleChange('appointmentType', value)}
                  options={appointmentTypes}
                  placeholder="Selecione o tipo de atendimento"
                  error={errors?.appointmentType}
                  required
                />
                
                {selectedAppointmentType && (
                  <div className="p-3 bg-muted rounded-md">
                    <div className="flex items-center justify-between text-sm">
                      <span className="font-medium">{selectedAppointmentType?.label}</span>
                      <span className="text-muted-foreground">{selectedAppointmentType?.duration}</span>
                    </div>
                    <p className="text-xs text-muted-foreground mt-1">
                      {selectedAppointmentType?.description}
                    </p>
                  </div>
                )}
              </div>

              {/* Time Selection */}
              {formData?.appointmentType && (
                <div className="space-y-3">
                  <Select
                    label="Horário Sugerido"
                    value={formData?.suggestedTime}
                    onChange={(value) => handleChange('suggestedTime', value)}
                    options={availableSlots}
                    placeholder={loading ? "Carregando horários..." : "Selecione um horário"}
                    disabled={loading}
                    error={errors?.suggestedTime}
                    required
                    loading={loading}
                  />
                  
                  {!loading && availableSlots?.length === 0 && formData?.appointmentType && (
                    <div className="flex items-center gap-2 p-3 bg-warning/10 rounded-md">
                      <AlertCircle className="w-4 h-4 text-warning" />
                      <p className="text-sm text-warning">
                        Nenhum horário disponível para este tipo de atendimento
                      </p>
                    </div>
                  )}
                  
                  {formData?.suggestedTime && (
                    <div className="flex items-center gap-2 p-3 bg-success/10 rounded-md">
                      <Calendar className="w-4 h-4 text-success" />
                      <p className="text-sm text-success">
                        Horário selecionado: {availableSlots?.find(slot => slot?.value === formData?.suggestedTime)?.label}
                      </p>
                    </div>
                  )}
                </div>
              )}

              {/* Message */}
              <div className="space-y-2">
                <label className="text-sm font-medium text-foreground">
                  Mensagem (Opcional)
                </label>
                <textarea
                  value={formData?.message}
                  onChange={(e) => handleChange('message', e?.target?.value)}
                  placeholder="Adicione informações adicionais sobre o que gostaria de discutir..."
                  className={cn(
                    "w-full min-h-24 px-3 py-2 rounded-md border text-sm",
                    "border-input bg-background text-foreground",
                    "placeholder:text-muted-foreground resize-none",
                    "focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2",
                    errors?.message && "border-destructive focus:ring-destructive"
                  )}
                  maxLength={maxCharacters}
                />
                <div className="flex justify-between text-xs">
                  <span className={errors?.message ? "text-destructive" : "text-muted-foreground"}>
                    {errors?.message || "Descreva brevemente o assunto do atendimento"}
                  </span>
                  <span className={cn(
                    "text-muted-foreground",
                    characterCount > maxCharacters * 0.9 && "text-warning",
                    characterCount >= maxCharacters && "text-destructive"
                  )}>
                    {characterCount}/{maxCharacters}
                  </span>
                </div>
              </div>

              {/* Action Buttons */}
              <div className="flex flex-col sm:flex-row gap-3 pt-4">
                <Button
                  type="button"
                  variant="outline"
                  onClick={handleClose}
                  className="flex-1"
                >
                  Cancelar
                </Button>
                
                <Button
                  type="submit"
                  className="flex-1"
                  disabled={!formData?.appointmentType || !formData?.suggestedTime}
                >
                  <Send className="w-4 h-4 mr-2" />
                  Solicitar Horário
                </Button>
              </div>
            </form>
          </>
        )}

        {step === 'loading' && (
          <div className="p-12 text-center">
            <div className="w-16 h-16 mx-auto mb-4 bg-primary/10 rounded-full flex items-center justify-center">
              <Loader2 className="w-8 h-8 text-primary animate-spin" />
            </div>
            <h3 className="text-lg font-semibold text-foreground mb-2">
              Enviando Solicitação
            </h3>
            <p className="text-sm text-muted-foreground">
              Aguarde enquanto processamos sua solicitação de atendimento...
            </p>
          </div>
        )}

        {step === 'success' && (
          <div className="p-12 text-center">
            <div className="w-16 h-16 mx-auto mb-4 bg-success/10 rounded-full flex items-center justify-center">
              <CheckCircle className="w-8 h-8 text-success" />
            </div>
            <h3 className="text-lg font-semibold text-foreground mb-2">
              Solicitação Enviada!
            </h3>
            <p className="text-sm text-muted-foreground mb-6">
              Sua solicitação foi enviada para {targetUser?.name}. 
              Você receberá uma resposta em até 24 horas.
            </p>
            
            <div className="space-y-3">
              <div className="p-3 bg-muted rounded-md text-left">
                <div className="text-xs text-muted-foreground mb-1">Resumo da Solicitação:</div>
                <div className="text-sm">
                  <div><strong>Tipo:</strong> {selectedAppointmentType?.label}</div>
                  <div><strong>Horário:</strong> {availableSlots?.find(slot => slot?.value === formData?.suggestedTime)?.label}</div>
                  {formData?.message && (
                    <div><strong>Mensagem:</strong> {formData?.message?.substring(0, 100)}...</div>
                  )}
                </div>
              </div>
              
              <Button onClick={handleClose} className="w-full">
                Fechar
              </Button>
            </div>
          </div>
        )}

        {step === 'error' && (
          <div className="p-12 text-center">
            <div className="w-16 h-16 mx-auto mb-4 bg-destructive/10 rounded-full flex items-center justify-center">
              <AlertCircle className="w-8 h-8 text-destructive" />
            </div>
            <h3 className="text-lg font-semibold text-foreground mb-2">
              Erro ao Enviar
            </h3>
            <p className="text-sm text-muted-foreground mb-6">
              Ocorreu um erro ao enviar sua solicitação. Por favor, tente novamente.
            </p>
            
            <div className="flex gap-3">
              <Button variant="outline" onClick={handleClose} className="flex-1">
                Cancelar
              </Button>
              <Button onClick={handleRetry} className="flex-1">
                Tentar Novamente
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AppointmentRequestModal;